import classnames from 'classnames';

import { rewriteImageSrc } from '@/utils/rewriteImageSrc';

import { computeFont } from '../utils/fonts';
import { makeHref } from './utils/makeHref';

import * as styles from './Paywall.css';
import type { PaywallElementProps } from './Paywall.types';

export function PaywallElement({
  element,
  className,
  theme,
  upgradePath,
  shouldUseUrl,
  upgradeUrl,
  isLive = true,
}: PaywallElementProps) {
  const { data } = element.attrs || {};
  const { title, description, cta_text, benefits, subheader, image_url, offer_id, style } = data || {
    title: 'Upgrade Now',
    description: 'Get access to premium content',
    cta_text: 'Subscribe',
    benefits: [],
    subheader: 'Subscription benefits',
    image_url: '',
    style: {
      show_image: true,
      background_color: '#ffffff',
      text_color: '#000',
      button_background_color: '#000000',
      button_text_color: '#ffffff',
      container_shadow: '0 0 #00000000',
    },
  };

  const hasBenefits = Array.isArray(benefits) && benefits.length > 0;
  const paragraphStyle = {
    fontFamily: `var(--wt-paragraph-font-family,${computeFont(theme?.paragraph_font_family)})`,
    fontSize: `var(--wt-paragraph-font-size,${theme?.paragraph_font_size || 0}px)`,
    lineHeight: `var(--wt-paragraph-line-height-size,${theme?.paragraph_line_height_size || 1.5})`,
  };

  const subheaderStyle = {
    fontFamily: `var(--wt-h4-font-family,${computeFont(theme?.h4_font_family)})`,
    fontSize: `var(--wt-h4-font-size,${theme?.h4_font_size || 0}px)`,
    lineHeight: `var(--wt-h4-line-height,${theme?.h4_line_height_size})`,
    fontWeight: '600',
    marginTop: '0px',
  };

  const href = shouldUseUrl ? upgradeUrl : makeHref(upgradePath, offer_id);

  return (
    <div
      className={classnames(className, styles.container, styles.textCenter, !isLive && 'pointer-events-none')}
      data-paywall-container
      style={{
        boxShadow: style?.container_shadow,
        backgroundColor: style?.background_color,
        color: style?.text_color,
        borderRadius: `var(--wt-button-border-radius,${theme?.button_border_radius || 0}px)`,
        padding: `calc(var(--wt-padding,${theme?.padding || 0}px) * 2)`,
      }}
    >
      {style?.show_image && image_url && <img src={rewriteImageSrc(image_url)} alt="logo" className={styles.logo} />}
      <h2
        className={styles.title}
        style={{
          fontFamily: `var(--wt-h2-font-family,${theme?.h2_font_family})`,
          fontSize: `var(--wt-h2-font-size,${theme?.h2_font_size || 0}px)`,
          lineHeight: `var(--wt-h2-line-height,${theme?.h2_line_height_size})`,
          paddingTop: `var(--wt-h2-padding-top, ${theme?.h2_padding_top || 0}px)`,
          paddingBottom: `var(--wt-h2-padding-bottom, ${theme?.h2_padding_bottom || 0}px)`,
          color: style?.text_color,
        }}
      >
        {title}
      </h2>
      <p
        className={styles.description}
        style={{
          ...paragraphStyle,
          color: style?.text_color,
          paddingTop: `var(--wt-paragraph-padding-top, ${theme?.paragraph_padding_top || 0}px)`,
          paddingBottom: `var(--wt-paragraph-padding-bottom, ${theme?.paragraph_padding_bottom || 0}px)`,
        }}
      >
        {description}
      </p>
      <div
        style={{
          paddingTop: `var(--wt-button-padding-top,${theme?.button_padding_top || 0}px)`,
          paddingBottom: `var(--wt-button-padding-bottom,${theme?.button_padding_bottom || 0}px)`,
        }}
      >
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.button}
          style={{
            margin: `var(--wt-button-margin-top,${theme?.button_margin_top || 0}px) auto var(--wt-button-margin-bottom,${theme?.button_margin_bottom || 0}px) auto`,
            padding: `var(--wt-button-padding-top,${theme?.button_padding_top}px) var(--wt-button-padding-right,${theme?.button_padding_right}px) var(--wt-button-padding-bottom,${theme?.button_padding_bottom}px) var(--wt-button-padding-left,${theme?.button_padding_left}px)`,
            backgroundColor: style?.button_background_color,
            fontFamily: `var(--wt-button-font-family,${theme?.button_font_family})`,
            color: style?.button_text_color,
            borderRadius: `var(--wt-button-border-radius,${theme?.button_border_radius || 0}px)`,
            fontSize: `var(--wt-button-font-size,${theme?.button_font_size || 16}px)`,
            fontWeight: `var(--wt-button-font-weight,${theme?.button_font_weight || 'normal'})`,
            lineHeight: `calc(var(--wt-button-font-size,${theme?.button_font_size || 16}px) + 2px)`,
          }}
        >
          {cta_text}
        </a>
      </div>
      {hasBenefits && (
        <>
          <h4
            className={styles.subheader}
            style={{
              ...subheaderStyle,
              color: style?.text_color,
              marginTop: `var(--wt-h4-padding-top,${theme?.h4_padding_top || 0}px)`,
              marginBottom: `var(--wt-h4-padding-bottom,${theme?.h4_padding_bottom || 0}px)`,
            }}
          >
            {subheader}:
          </h4>
          <div className={styles.featureList}>
            {benefits.map((benefit: string) => (
              <p
                key={benefit}
                className={styles.featureListItem}
                style={{
                  ...paragraphStyle,
                  color: style?.text_color,
                  padding: `4px var(--wt-padding,${theme?.padding}px)`,
                }}
              >
                • {benefit}
              </p>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
